import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import { baseurl } from '../../baseurl';
import { 
  ArrowLeft, Ticket, Calendar, 
  Layers, Save, Hash, CheckCircle2
} from 'lucide-react';

const CreateCoupon = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminToken = localStorage.getItem('adminToken');
  
  // Detect Edit Mode
  const couponId = searchParams.get('id');
  const isEdit = searchParams.get('action') === 'edit' && couponId;

  const planOptions = [
    { value: 'access', label: 'Access Plans' },
    { value: 'pro', label: 'Pro Plans' }, 
  ];

  const durationOptions = [
    { value: '1', label: 'First billing cycle only' },
    { value: '3', label: 'First 3 months' },
    { value: '6', label: 'First 6 months' }, 
    { value: '99', label: 'Lifetime' }, 
  ];

  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    startDate: new Date(),
    endDate: null,
    usageLimit: '',
    description: '',
    applyToType: planOptions[0], 
    recurringLimit: durationOptions[0],
    is_active: true
  });

  // 1. Fetch Data if in Edit Mode
  useEffect(() => {
    if (isEdit) {
      const fetchCoupon = async () => {
        try {
          const res = await axios.get(`${baseurl}getCouponById/${couponId}`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          if (res.data.success) {
            const c = res.data.coupon;
            
            // Map raw values back to Select Objects
            const selectedPlan = planOptions.find(opt => opt.value === c.apply_to_type) || planOptions[0];
            const selectedDuration = durationOptions.find(opt => opt.value === String(c.recurring_limit)) || durationOptions[0];

            setFormData({
              code: c.code,
              discountType: c.discount_type,
              discountValue: c.discount_value,
              startDate: new Date(c.start_date),
              endDate: c.end_date ? new Date(c.end_date) : null,
              usageLimit: c.usage_limit || '',
              description: c.description || '',
              applyToType: selectedPlan,
              recurringLimit: selectedDuration,
              is_active: c.is_active
            });
          }
        } catch (err) {
          Swal.fire('Error', 'Could not fetch coupon details', 'error');
        }
      };
      fetchCoupon();
    }
  }, [isEdit, couponId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData(prev => ({ ...prev, [field]: selectedOption }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const generateCode = () => {
    const randomCode = Math.random().toString(36).substring(2, 10).toUpperCase();
    setFormData(prev => ({ ...prev, code: randomCode }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!formData.endDate) {
        return Swal.fire('Missing Date', 'Please select a campaign end date', 'warning');
    }
    
    const submissionData = {
        ...formData,
        applyToType: formData.applyToType.value,
        recurringLimit: formData.recurringLimit.value
    };

    Swal.fire({ title: isEdit ? 'Updating...' : 'Deploying...', didOpen: () => Swal.showLoading() });

    try {
      const url = isEdit ? `${baseurl}updateCoupon/${couponId}` : `${baseurl}createSubscriptionCoupon`;
      const method = isEdit ? 'put' : 'post';

      const res = await axios[method](url, submissionData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (res.data.success) {
        Swal.fire('Success', isEdit ? 'Coupon Updated' : 'Coupon Live', 'success');
        navigate('/coupons');
      }
    } catch (err) {
      Swal.fire('Error', 'Action failed. Check console for details.', 'error');
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      background: "#f8f9fa",
      border: state.isFocused ? "1px solid #4318FF" : "none",
      borderRadius: "10px",
      padding: "5px",
      boxShadow: state.isFocused ? "0px 4px 12px rgba(67, 24, 255, 0.1)" : "none",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#4318FF" : state.isFocused ? "#EBEBFF" : "white",
      color: state.isSelected ? "white" : "#1B2559",
      fontSize: "14px",
    })
  };

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', minHeight: '100vh', display: 'flex' }}>
        <div className="p-4" style={{ flex: 1, overflowY: 'auto' }}>
          
          <div className="d-flex align-items-center mb-4">
            <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm rounded-circle me-3">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>
                {isEdit ? 'Edit Coupon' : 'Subscription Coupon'}
              </h3>
              <p className="text-muted small m-0">
                {isEdit ? `Modifying coupon: ${formData.code}` : 'Create recurring or one-time subscription discounts'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '20px' }}>
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary-soft p-2 rounded-3 me-3">
                      <Ticket size={24} className="text-primary" />
                    </div>
                    <h5 className="fw-bold m-0">General Configuration</h5>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-7">
                      <label className="form-label small fw-bold text-muted text-uppercase">Coupon Code</label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-0"><Hash size={16}/></span>
                        <input 
                            type="text" 
                            name="code" 
                            className="form-control bg-light border-0" 
                            value={formData.code} 
                            onChange={handleInputChange} 
                            required 
                            disabled={isEdit} // Usually codes shouldn't change after creation
                        />
                        {!isEdit && <button type="button" className="btn btn-outline-primary px-3" onClick={generateCode}>Auto-Gen</button>}
                      </div>
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small fw-bold text-muted text-uppercase">Discount Type</label>
                      <select className="form-select bg-light border-0" name="discountType" value={formData.discountType} onChange={handleInputChange} style={{height: '50px', borderRadius: '10px'}}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted text-uppercase">Discount Value</label>
                      <div className="input-group">
                        <input type="number" name="discountValue" value={formData.discountValue} className="form-control bg-light border-0" placeholder="0.00" onChange={handleInputChange} required />
                        <span className="input-group-text bg-light border-0">{formData.discountType === 'percentage' ? '%' : '$'}</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted text-uppercase">Total Redemption Limit</label>
                      <input type="number" name="usageLimit" value={formData.usageLimit} className="form-control bg-light border-0" placeholder="Infinite if empty" onChange={handleInputChange}/>
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-bold text-muted text-uppercase">Public Description</label>
                      <textarea name="description" value={formData.description} className="form-control bg-light border-0" rows="2" placeholder="e.g. Save 20% on..." onChange={handleInputChange}></textarea>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary-soft p-2 rounded-3 me-3">
                      <Layers size={24} className="text-primary" />
                    </div>
                    <h5 className="fw-bold m-0">Subscription Constraints</h5>
                  </div>
                  
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted">Applicable Plans</label>
                      <Select
                        options={planOptions}
                        value={formData.applyToType}
                        onChange={(opt) => handleSelectChange(opt, 'applyToType')}
                        styles={customSelectStyles}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted">Discount Duration</label>
                      <Select
                        options={durationOptions}
                        value={formData.recurringLimit}
                        onChange={(opt) => handleSelectChange(opt, 'recurringLimit')}
                        styles={customSelectStyles}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card border-0 shadow-sm p-4 mb-4 text-center overflow-hidden" style={{ borderRadius: '20px', background: '#FFF' }}>
                  <div className="mb-3">
                    <div className="bg-primary-soft d-inline-block p-3 rounded-circle mb-2">
                      <CheckCircle2 size={32} className="text-primary" />
                    </div>
                    <h5 className="fw-bold mb-0 text-dark">Checkout Preview</h5>
                  </div>
                  
                  <div className="p-3 mb-3" style={{ border: '2px dashed #E0E5F2', borderRadius: '15px' }}>
                    <div className="small text-muted text-uppercase fw-bold mb-1">Promo Applied</div>
                    <h3 className="fw-bold text-primary mb-2">{formData.code || 'CODE'}</h3>
                    <div className="h4 fw-bold mb-0">
                        {formData.discountValue || '0'}{formData.discountType === 'percentage' ? '%' : '$'} OFF
                    </div>
                    <div className="text-muted small mt-1">
                        {formData.recurringLimit?.label}
                    </div>
                  </div>
                  
                  <button type="submit" className="btn btn-primary w-100 rounded-pill fw-bold py-3 shadow-sm mt-2">
                    <Save size={18} className="me-2" /> {isEdit ? 'Update Coupon' : 'Publish Coupon'}
                  </button>
                </div>

                <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                  <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <Calendar size={18} className="me-2 text-primary" /> Campaign Dates
                  </h6>
                  
                  <label className="form-label extra-small fw-bold text-muted text-uppercase mb-1">Start Date</label>
                  <DatePicker
                    selected={formData.startDate}
                    onChange={(date) => handleDateChange(date, 'startDate')}
                    className="form-control bg-light border-0 mb-3"
                  />

                  <label className="form-label extra-small fw-bold text-muted text-uppercase mb-1">Expiration Date</label>
                  <DatePicker
                    selected={formData.endDate}
                    onChange={(date) => handleDateChange(date, 'endDate')}
                    minDate={formData.startDate}
                    placeholderText="Select end date"
                    className="form-control bg-light border-0"
                    isClearable
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCoupon;